export interface PopoverHandle {
  panel: HTMLElement;
  setOpen(open: boolean): void;
  isOpen(): boolean;
  destroy(): void;
}

export interface AttachPopoverOptions {
  panelClassName?: string;
}

/**
 * Wraps `anchor` in a positioned `.kit-popover-host` and attaches a toggleable
 * popover panel anchored to its bottom-right. Anchor click toggles; outside
 * click and Escape close. The caller owns the panel's content.
 */
export function attachPopover(anchor: HTMLElement, opts: AttachPopoverOptions = {}): PopoverHandle {
  const host = document.createElement('span');
  host.className = 'kit-popover-host';
  anchor.replaceWith(host);
  host.appendChild(anchor);

  const panel = document.createElement('div');
  panel.className = `kit-popover ${opts.panelClassName ?? ''}`.trim();
  panel.setAttribute('data-open', 'false');
  panel.setAttribute('role', 'dialog');
  host.appendChild(panel);

  anchor.setAttribute('aria-haspopup', 'dialog');
  anchor.setAttribute('aria-expanded', 'false');

  let open = false;
  const setOpen = (next: boolean): void => {
    open = next;
    panel.setAttribute('data-open', open ? 'true' : 'false');
    anchor.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  const onAnchorClick = (e: MouseEvent): void => {
    e.stopPropagation();
    setOpen(!open);
  };
  const onDocClick = (e: MouseEvent): void => {
    if (!open) return;
    const target = e.target as Node | null;
    if (target && host.contains(target)) return;
    setOpen(false);
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && open) setOpen(false);
  };

  anchor.addEventListener('click', onAnchorClick);
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKeyDown);

  return {
    panel,
    setOpen,
    isOpen: () => open,
    destroy(): void {
      anchor.removeEventListener('click', onAnchorClick);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKeyDown);
      panel.remove();
      host.replaceWith(anchor);
    },
  };
}
